import { useState, useEffect } from 'react';
import { Search, Bell, TrendingUp, BarChart3, Activity } from 'lucide-react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../hooks/useAuth';
import { AgentInteraction, QueryTopic } from '../../types/agent';
import { Badge, EmptyState } from '../shared';

export function QueryMonitor() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [interactions, setInteractions] = useState<AgentInteraction[]>([]);
  const [topTopics, setTopTopics] = useState<QueryTopic[]>([]);
  const [volumeBySystem, setVolumeBySystem] = useState<Record<string, number>>({});
  const [recentQueries, setRecentQueries] = useState<AgentInteraction[]>([]);

  useEffect(() => {
    loadData();
  }, [user]);

  const loadData = async () => {
    if (!user) return;

    try {
      const q = query(
        collection(db, 'agentInteractions'),
        where('userId', '==', user.uid)
      );
      const snapshot = await getDocs(q);
      const interactionsData = snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }) as AgentInteraction);
      setInteractions(interactionsData);

      // Calculate volume by AI system
      const systemVolume: Record<string, number> = {};
      interactionsData.forEach(i => {
        systemVolume[i.aiSystem] = (systemVolume[i.aiSystem] || 0) + 1;
      });
      setVolumeBySystem(systemVolume);

      // Extract topics (simple keyword extraction - in production would use NLP)
      const topicCounts: Record<string, QueryTopic> = {};
      interactionsData.forEach(i => {
        const words = i.query.toLowerCase().split(/\s+/);
        words.forEach(word => {
          if (word.length > 4) { // Filter out short words
            if (!topicCounts[word]) {
              topicCounts[word] = {
                topic: word,
                count: 0,
                lastSeen: i.timestamp
              };
            }
            topicCounts[word].count++;
            if (i.timestamp.toDate() > topicCounts[word].lastSeen.toDate()) {
              topicCounts[word].lastSeen = i.timestamp;
            }
          }
        });
      });

      const topics = Object.values(topicCounts)
        .sort((a, b) => b.count - a.count)
        .slice(0, 20);
      setTopTopics(topics);

      // Recent queries
      const recent = interactionsData
        .sort((a, b) => b.timestamp.toDate().getTime() - a.timestamp.toDate().getTime())
        .slice(0, 20);
      setRecentQueries(recent);

    } catch (error) {
      console.error('Error loading query monitor data:', error);
    } finally {
      setLoading(false);
    }
  };

  const totalQueries = interactions.length;
  const systemsWithHighestVolume = Object.entries(volumeBySystem)
    .sort((a, b) => b[1] - a[1]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-t3-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Navigation Bar */}
      <div className="bg-shell-navy border-b border-gray-700 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className="text-gray-400 text-sm">Agent</span>
          <span className="text-gray-600">/</span>
          <span className="text-white text-sm font-medium">Query Monitor</span>
        </div>

        <div className="flex items-center gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              placeholder="Search queries..."
              className="pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-500 w-64 focus:outline-none focus:border-t3-primary"
            />
          </div>
          <span className="px-3 py-1 bg-orange-600 text-white text-xs font-semibold rounded uppercase">Growth</span>
          <button className="relative p-2 text-gray-400 hover:text-white">
            <Bell className="w-5 h-5" />
            <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-6 py-8">
        <div className="mb-6">
          <p className="text-xs font-semibold text-t3-primary uppercase tracking-wide mb-1">Kodiac Agent</p>
          <h1 className="text-3xl font-semibold text-gray-900">Query Monitor</h1>
          <p className="text-sm text-gray-600 mt-1">Track query patterns and user interests across AI platforms</p>
        </div>

        <div className="grid grid-cols-2 gap-6 mb-6">
          {/* Top Query Topics */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-5 h-5 text-t3-primary" />
              <h2 className="text-base font-semibold text-gray-900">Top Query Topics</h2>
            </div>

            {topTopics.length === 0 ? (
              <EmptyState
                icon={TrendingUp}
                title="No query data"
                description="Query topics will appear here as users interact with your agent"
              />
            ) : (
              <div className="space-y-3">
                {topTopics.slice(0, 10).map((topic, index) => (
                  <div key={topic.topic} className="flex items-center justify-between p-3 bg-surface-warm rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center justify-center w-8 h-8 bg-t3-primary text-white rounded-full text-sm font-semibold">
                        {index + 1}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900 capitalize">{topic.topic}</p>
                        <p className="text-xs text-gray-600">
                          Last seen: {topic.lastSeen.toDate().toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-semibold text-t3-primary">{topic.count}</div>
                      <div className="text-xs text-gray-500">queries</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Volume by AI System */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 className="w-5 h-5 text-t3-primary" />
              <h2 className="text-base font-semibold text-gray-900">Volume by AI System</h2>
            </div>

            {Object.keys(volumeBySystem).length === 0 ? (
              <EmptyState
                icon={BarChart3}
                title="No interaction data"
                description="Query volume will appear here as users interact across platforms"
              />
            ) : (
              <div className="space-y-4">
                {systemsWithHighestVolume.map(([system, count]) => {
                  const percentage = Math.round((count / totalQueries) * 100);
                  return (
                    <div key={system}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-900 capitalize">{system}</span>
                        <span className="text-sm font-semibold text-t3-primary">{count}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-3">
                        <div
                          className="bg-t3-primary h-3 rounded-full"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                      <div className="text-xs text-gray-600 mt-1">{percentage}% of total queries</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Live Query Feed */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-t3-primary" />
              <h2 className="text-base font-semibold text-gray-900">Live Query Feed</h2>
            </div>
            <span className="text-sm text-gray-600">{recentQueries.length} recent</span>
          </div>

          {recentQueries.length === 0 ? (
            <EmptyState
              icon={Activity}
              title="No queries yet"
              description="Recent queries will appear here in real-time"
            />
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {recentQueries.map(query => (
                <div
                  key={query.id}
                  className="flex items-start justify-between p-3 bg-surface-warm rounded-lg border border-gray-200 hover:border-t3-primary transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant="primary">{query.aiSystem}</Badge>
                      {query.intentLevel !== 'none' && (
                        <Badge
                          variant={
                            query.intentLevel === 'high'
                              ? 'critical'
                              : query.intentLevel === 'medium'
                              ? 'warning'
                              : 'primary'
                          }
                        >
                          {query.intentLevel} intent
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-gray-900 line-clamp-2">{query.query}</p>
                  </div>
                  <span className="text-xs text-gray-600 ml-4 whitespace-nowrap">
                    {query.timestamp.toDate().toLocaleTimeString()}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Insights */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-sm font-semibold text-blue-900 mb-2">Query Insights</h3>
          <ul className="space-y-1 text-sm text-blue-800">
            <li>• Monitor trending topics to identify content gaps and opportunities</li>
            <li>• Platform distribution shows where your audience is most active</li>
            <li>• Use query patterns to optimize your agent's knowledge base</li>
            <li>• High-frequency topics may indicate areas for dedicated content creation</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
